/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import isNil from 'lodash.isnil';
import uniq from 'lodash.uniq';
import {GenericObject} from '@ion/core/src/types/entities';

/**
 * Provides a convenience utility which splits an array into a multi-dimensional array
 * where each element is an array of the given size.
 *
 * ```typescript
 *
 *   import {batch} from '@ion/core/src/utils/';
 *
 *   const batches = batch([1, 2, 3, 4, 5, 6, 7, 8, 9, 10], 5);
 *   batches.forEach(batch => {
 *     console.log(batch);
 *   })
 *   // 1, 2, 3, 4, 5
 *   // 6, 7, 8, 9, 10
 *
 * ```
 */
export const batch = (items: any[], size: number, results = []) => {
  return items.length > 0 ? batch(
    items?.slice(size), size, results.concat([items?.slice(0, size)])
  ) : results;
};

/**
 * Provides a convenience utility to sort an array.
 *
 * ```typescript
 *
 *   import {sortBy} from '@ion/core/src/utils/ArrayUtil';
 *
 *   const arrayToBeSorted = [{value: 10}, {value: 100}, {value: 50}, {value: 90}, {value: 30}];
 *   const prop = 'value';
 *   const isDescending = true;
 *   const list = sortBy(arrayToBeSorted, prop, isDescending)
 *
 * ```
 */
export const sortBy = (
  array: any[],
  prop: string,
  isDescending = false
): any[] => {
  return array.sort((a, b) => isDescending ? b[prop] - a[prop] : a[prop] - b[prop]);
};

/**
 * Provides a convenience utility to sort an array by a string property.
 *
 * ```typescript
 *
 *   import {sortByString} from '@ion/core/src/utils/ArrayUtil';
 *
 *   const arrayToBeSorted = [{value: 'foo'}, {value: 'bar'}, {value: 'baz'}];
 *   const prop = 'value';
 *   const isDescending = true;
 *   const list = sortByString(arrayToBeSorted, prop, isDescending)
 *
 * ```
 */
export const sortByString = (
  array: any[],
  prop: string,
  isDescending = false
): any[] => {
  return array?.sort((a, b) => isDescending ? b[prop].localeCompare(a[prop]) : a[prop].localeCompare(b[prop]));
};

/**
 * Joins a list of objects by the property name that is passed
 *
 * ```typescript
 *
 *  import {joinByObjectProperty} from '@ion/core/src/utils/';
 *
 *  const arr = [
 *    {name: 'Muthu,kumar', age: 24},
 *    {name: 'Asif', age: 32}
 *  ];
 *
 *  joinByObjectProperty(arr, 'name'); // Muthu, kumar, Asif
 *  joinByObjectProperty(arr, 'name', true); // kumar Muthu, Asif
 *
 * ```
 * @param arr Array of object
 * @param property The property for mapping
 * @param reversePropertyValue boolean value force to reverse the
 * property value if that value is seaparated by comma.
 */
export const joinByObjectProperty = (
  arr: GenericObject[],
  property: string,
  reversePropertyValue = false
) => {
  return arr?.map((item) => {
    return reversePropertyValue ? item[property]?.split(',').reverse().join(' ') : item[property];
  }).join(', ').trim();
};

/**
 * Given a an array, determines if the array is empty (i.e. does not have any elements)
 * or not.
 *
 * ```typescript
 *   import {isArrayEmpty} from '@ion/core/src/utils/ArrayUtil';
 *
 *   isArrayEmpty({}); // true
 *   isArrayEmpty(null); // true
 *   isArrayEmpty({id: 0}); // false
 *
 * ```
 *
 * @method   isArrayEmpty
 * @param    {Array} arr The array that is to be inspected.
 * @returns  {boolean} True if the array is not empty, otherwise, false.
 */
export const isArrayEmpty = (arr: any[]): boolean => {
  return arr ? Boolean(arr.length === 0) : true;
};

/**
 * Simple utility to remove an item from an array, although it must be able
 * to be found by using indexOf
 *
 * ```typescript
 *   import {removeItemFromArray} from '@ion/core/src/utils/';
 *
 *   removeItemFromArray(['Ryan', 'Eric', 'Mark'], 'Ryan') // ['Eric', 'Mark']
 *   removeItemFromArray([21, 54, 109, 34], 109) // [21, 54, 34]]
 * ```
 */
export const removeItemFromArray = (arr: any[], item: any) => {
  const index = arr.indexOf(item);
  if (index > -1) {
    arr.splice(index, 1);
  }
  return arr;
};

// defines the updateItemInArray optional arguments ...
export interface UpdateItemInArrayOptions {
  identifier?: string;
  updater?: (updated: any, previous: any) => any
}

/**
 * Provides a utility which allows for updating an existing item in an array.
 *
 * ```typescript
 *   import {updateItemInArray} from '@ion/core/src/utils/';
 *
 *   const items = [
 *     {id: 100, name: 'Item 100'},
 *     {id: 200, name: 'Item 200'},
 *     {id: 300, name: 'Item 300'}
 *   ];
 *   const item = {id: 200, name: 'Item 300 (Updated)'};
 *   info(updateItemInArray(items, item)[2].name); // 'Item 300 (Updated)'
 * ```
 *
 * @param items An array of items containing the item which is to be updated.
 * @param updated The current value of the item to be updated
 * @param options.identifier An optional field to be used for identifying the
 *        item that is to be updated (defaults to an 'id' field).
 * @param options.updater An optional updater function to be used for providing
 *        the update to the item. This option is useful for providing custom
 *        updates such as merging, augmenting, etc.
 */
export const updateItemInArray = (
  items: any[], updated: any, {identifier = 'id', updater}: UpdateItemInArrayOptions = {}) => {
  const id = updated[identifier];
  const index: number = items?.findIndex(element => element[identifier] === id);
  if (!isNaN(index)) {
    items.splice(index, 1, updater?.(updated, items[index]) || updated);
  }
  return items;
};

/**
 * Simple utility to remove empty item from an array, although it must be able
 * to be found by using filter and it will return uniq values from array
 *
 * ```typescript
 *   import {getUniqueArray} from '@ion/core/src/utils/';
 *
 *   getUniqueArray(['21', '34', '109', '34', '', 15]) // [21, 34, 109, 15]]
 * ```
 */
export const getUniqueArray = (arr) => {
  const filteredArr = arr.filter(item => item !== '');
  return uniq(filteredArr);
};

/**
 * Given a an array, determines if the array has any undefined or null elements
 * or not.
 *
 * ```typescript
 *   import {isArrayEmpty} from '@ion/core/src/utils/ArrayUtil';
 *
 *   isArrayEmpty([1, undefined]); // true
 *   isArrayEmpty([null]); // true
 *   isArrayEmpty([1, false, '']); // false
 *
 * ```
 *
 * @method   isArrayDeepEmpty
 * @param    {Array} arr The array that is to be inspected.
 * @returns  {boolean} True if the array is not empty, otherwise, false.
 */
export const isArrayDeepEmpty = (arr: any[]): boolean => {
  const n = Array.isArray(arr) ? arr.length : 0;
  return n ? !!(arr.filter(isNil).length === n) : true;
};

/**
 * Simple utility to get max value from an array
 *
 * ```typescript
 *   import {getMaxValueInArray} from '@ion/core/src/utils/';
 *
 *   getMaxValueInArray([21, 34, 109, 15]) // 109
 * ```
 */
export const getMaxValueInArray = (arr: number[]) => {
  return arr.reduce((previous, current) => {
    return previous > current ? previous : current;
  });
};

/**
 * Simple utility to determine if an array has duplicate values (shallow check).
 *
 * ```typescript
 *   import {getArrayDuplicates} from '@ion/core/src/utils/';
 *
 *   const duplicates = getArrayDuplicates(['foo', 'bar', 'foo']); // ['foo']
 * ```
 */
export const getArrayDuplicates = (arr: any[] = []): any[] => {
  return arr.filter((item, index) => arr.indexOf(item) !== index);
};

/**
 * Given a group of array, determines if any array has a length (i.e. has one or more elements)
 * or not.
 *
 * ```typescript
 *   import {hasLength} from '@ion/core/src/utils/';
 *
 *   hasLength([1, 2, 3]); // true
 *   hasLength(null); // false
 *   hasLength([]); // false
 *   hasLength([null, null]); // false
 *
 * ```
 *
 * @method   hasLength
 * @param    {Array} arr The array that is to be inspected.
 * @returns  {boolean} True if the array is not empty, otherwise, false.
 */
export const hasLength = (...items): boolean => !!items?.find(item => item?.length);

/**
 * The pickLastItem utility function will return the last item
 * of the index in the safer way that support in different browsers.
 *
 * ```typescript
 *
 *  import {pickLastItem} from '@ion/core/src/utils/';
 *
 *  const lastItem = pickLastItem([1, 2, 3]); // 3
 *
 * ```
 */
export const pickLastItem = (item: any[]) => (
  item.at ? item.at(-1) : item[item.length - 1]
);

/**
 * The convertStringsToNumbers utility function will return the array elements from string to number.
 *
 * ```typescript
 *
 *  import {convertStringsToNumbers} from '@ion/core/src/utils/';
 *
 *  const list = convertStringsToNumbers(["1", "2", "3"]); // [1, 2, 3]
 *
 * ```
 */
export const convertStringsToNumbers = (arr: any[], property?: string): number[] => (
  arr.map(ele => Number(property ? ele[property] : ele))
);
